import React, { useState, useEffect, useCallback } from 'react';
import { P, font } from '../styles/theme';
import { Spinner, Card } from '../components/Shared';
import { DatKhamService } from '../services/api';
import logo from '../assets/logo.png';

const GENDERS = [
  { value: '0', label: 'Nam' },
  { value: '1', label: 'Nữ' },
  { value: '2', label: 'Khác' },
];

const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const emptyForm = () => ({
  tenBenhNhan: '',
  ngaySinh: '',
  idGioiTinh: '0',
  soCccd: '',
  dienThoai: '',
  diaChi: '',
  danToc: '',
  ngheNghiep: '',
  ngayDatKham: todayStr(),
  idBacSyDatKham: '',
  idDichVuKcb: '',
});

const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ` +
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// IMPORTANT: Field phải định nghĩa ở module scope (ngoài component).
// Nếu để bên trong, mỗi lần re-render sẽ tạo lại component -> input bị remount -> mất focus (chỉ gõ được 1 ký tự).
const labelStyle = { display: 'block', fontSize: 13, color: P.textDim, marginBottom: 6, fontWeight: 600 };

function Field({ lbl, required, children }) {
  return (
    <div>
      <label style={labelStyle}>{lbl}{required && <span style={{ color: P.danger }}> *</span>}</label>
      {children}
    </div>
  );
}

export default function BookingPage() {
  const [form, setForm] = useState(emptyForm);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneWarn, setPhoneWarn] = useState(null); // { count, ngayDatKham }
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [docs, svcs] = await Promise.all([
          DatKhamService.getDoctors(),
          DatKhamService.getServices(),
        ]);
        if (!cancelled) {
          setDoctors(docs || []);
          setServices(svcs || []);
        }
      } catch (err) {
        if (!cancelled) setError('Không tải được danh mục bác sỹ / dịch vụ. Vui lòng thử lại.');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const checkPhone = useCallback(async (phone, date) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length < 9) { setPhoneWarn(null); return; }
    setCheckingPhone(true);
    try {
      const res = await DatKhamService.checkPhone(phone.trim(), date);
      setPhoneWarn(res && res.exists ? { count: res.count, ngayDatKham: res.ngayDatKham } : null);
    } catch {
      setPhoneWarn(null);
    } finally {
      setCheckingPhone(false);
    }
  }, []);

  const onPhoneBlur = () => checkPhone(form.dienThoai, form.ngayDatKham);

  const submit = async () => {
    setError('');
    if (!form.tenBenhNhan.trim()) { setError('Vui lòng nhập họ tên bệnh nhân.'); return; }
    const digits = form.dienThoai.replace(/\D/g, '');
    if (digits.length < 9 || digits.length > 11) { setError('Số điện thoại không hợp lệ.'); return; }

    setSubmitting(true);
    try {
      const payload = {
        TenBenhNhan: form.tenBenhNhan.trim(),
        NgaySinh: form.ngaySinh || null,
        IdGioiTinh: form.idGioiTinh || null,
        SoCccd: form.soCccd.trim() || null,
        DienThoai: form.dienThoai.trim(),
        DiaChi: form.diaChi.trim() || null,
        DanToc: form.danToc.trim() || null,
        NgheNghiep: form.ngheNghiep.trim() || null,
        NgayDatKham: form.ngayDatKham || null,
        IdBacSyDatKham: form.idBacSyDatKham ? Number(form.idBacSyDatKham) : null,
        IdDichVuKcb: form.idDichVuKcb ? Number(form.idDichVuKcb) : null,
      };
      await DatKhamService.create(payload);
      setDone(true);
    } catch (err) {
      if (!err.response) setError('Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng.');
      else setError(err.response?.data?.message || 'Đặt khám thất bại. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNew = () => {
    setForm(emptyForm());
    setPhoneWarn(null);
    setError('');
    setDone(false);
  };

  // ===== shared styles =====
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '11px 13px',
    background: P.inputBg, border: '1px solid ' + P.border, borderRadius: 9,
    color: P.text, fontSize: 14, fontFamily: font, outline: 'none',
    transition: 'border-color .2s, box-shadow .2s',
  };
  const onFocus = (e) => { e.target.style.borderColor = P.primary; e.target.style.boxShadow = '0 0 0 3px rgba(0,114,188,0.1)'; };
  const onBlur = (e) => { e.target.style.borderColor = P.border; e.target.style.boxShadow = 'none'; };

  return (
    <div style={{ minHeight: '100vh', background: P.bg, fontFamily: font, color: P.text }}>
      {/* Header */}
      <header style={{
        background: P.headerBg, padding: '14px 24px',
        display: 'flex', alignItems: 'center', gap: 14,
        boxShadow: '0 2px 8px rgba(0,114,188,0.15)',
      }}>
        <img src={logo} alt="PK Đa Khoa KCN Quế Võ" style={{ height: 40 }} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: P.headerText, letterSpacing: 0.5 }}>PHÒNG KHÁM ĐA KHOA KCN QUẾ VÕ</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Đăng ký đặt khám trực tuyến</div>
        </div>
      </header>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 18px 60px' }}>
        {done ? (
          <Card style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#e8f8ef',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={P.success} strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, color: P.text }}>Đặt khám thành công!</h2>
            <p style={{ color: P.textMuted, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              Phiếu đặt khám đã được ghi nhận. Vui lòng đến quầy tiếp nhận đúng ngày hẹn để hoàn tất thủ tục.
            </p>
            <button onClick={resetForNew} style={{
              padding: '12px 28px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${P.primary}, ${P.primaryDark})`,
              color: P.white, fontSize: 15, fontWeight: 700, fontFamily: font,
            }}>Đặt phiếu mới</button>
          </Card>
        ) : (
          <Card style={{ padding: '24px 26px' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20, color: P.text }}>Phiếu đăng ký đặt khám</h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: P.textMuted }}>
              Vui lòng điền thông tin bên dưới. Các trường có dấu <span style={{ color: P.danger }}>*</span> là bắt buộc.
            </p>

            {/* Phone duplicate warning */}
            {phoneWarn && (
              <div style={{
                padding: '11px 14px', borderRadius: 9, marginBottom: 16,
                background: '#fef3e2', border: '1px solid #f6d089', color: '#9a6a00',
                fontSize: 13, display: 'flex', alignItems: 'center', gap: 9,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                  Số điện thoại này đã có <strong>{phoneWarn.count}</strong> phiếu đặt khám trong ngày
                  {phoneWarn.ngayDatKham ? <> (lần gần nhất {fmtDateTime(phoneWarn.ngayDatKham)})</> : null}.
                  Bạn vẫn có thể tiếp tục nếu muốn đặt thêm.
                </span>
              </div>
            )}

            {loadingMeta ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field lbl="Họ và tên bệnh nhân" required>
                    <input style={inputStyle} value={form.tenBenhNhan} onChange={set('tenBenhNhan')}
                      onFocus={onFocus} onBlur={onBlur} placeholder="VD: Nguyễn Văn A" />
                  </Field>
                </div>

                <Field lbl="Số điện thoại" required>
                  <div style={{ position: 'relative' }}>
                    <input style={inputStyle} type="tel" value={form.dienThoai} onChange={set('dienThoai')}
                      onFocus={onFocus} onBlur={(e) => { onBlur(e); onPhoneBlur(); }} placeholder="VD: 0912345678" />
                    {checkingPhone && (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        <Spinner size={16} />
                      </div>
                    )}
                  </div>
                </Field>

                <Field lbl="Ngày sinh">
                  <input style={inputStyle} type="date" value={form.ngaySinh} onChange={set('ngaySinh')}
                    onFocus={onFocus} onBlur={onBlur} max={todayStr()} />
                </Field>

                <Field lbl="Giới tính">
                  <select style={inputStyle} value={form.idGioiTinh} onChange={set('idGioiTinh')} onFocus={onFocus} onBlur={onBlur}>
                    {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </Field>

                <Field lbl="Số CCCD">
                  <input style={inputStyle} value={form.soCccd} onChange={set('soCccd')}
                    onFocus={onFocus} onBlur={onBlur} placeholder="12 số" maxLength={12} />
                </Field>

                <Field lbl="Dân tộc">
                  <input style={inputStyle} value={form.danToc} onChange={set('danToc')}
                    onFocus={onFocus} onBlur={onBlur} placeholder="VD: Kinh" />
                </Field>

                <Field lbl="Nghề nghiệp">
                  <input style={inputStyle} value={form.ngheNghiep} onChange={set('ngheNghiep')}
                    onFocus={onFocus} onBlur={onBlur} placeholder="VD: Công nhân" />
                </Field>

                <div style={{ gridColumn: '1 / -1' }}>
                  <Field lbl="Địa chỉ">
                    <input style={inputStyle} value={form.diaChi} onChange={set('diaChi')}
                      onFocus={onFocus} onBlur={onBlur} placeholder="Số nhà, đường, phường/xã, quận/huyện, tỉnh/TP" />
                  </Field>
                </div>

                <Field lbl="Ngày đặt khám">
                  <input style={inputStyle} type="date" value={form.ngayDatKham}
                    onChange={(e) => { set('ngayDatKham')(e); if (form.dienThoai) checkPhone(form.dienThoai, e.target.value); }}
                    onFocus={onFocus} onBlur={onBlur} min={todayStr()} />
                </Field>

                <Field lbl="Bác sỹ khám">
                  <select style={inputStyle} value={form.idBacSyDatKham} onChange={set('idBacSyDatKham')} onFocus={onFocus} onBlur={onBlur}>
                    <option value="">— Chọn bác sỹ —</option>
                    {doctors.map((d) => (
                      <option key={d.idNhanVien} value={d.idNhanVien}>{d.tenNhanVien}</option>
                    ))}
                  </select>
                </Field>

                <div style={{ gridColumn: '1 / -1' }}>
                  <Field lbl="Dịch vụ khám">
                    <select style={inputStyle} value={form.idDichVuKcb} onChange={set('idDichVuKcb')} onFocus={onFocus} onBlur={onBlur}>
                      <option value="">— Chọn dịch vụ khám —</option>
                      {services.map((s) => (
                        <option key={s.idDichVuKcb} value={s.idDichVuKcb}>
                          {s.tenDichVuKcb}{s.donGia ? ` — ${Number(s.donGia).toLocaleString('vi-VN')}đ` : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
            )}

            {error && (
              <div style={{
                marginTop: 18, padding: '11px 14px', borderRadius: 8,
                background: '#fde8e8', border: '1px solid #f5c6cb', color: P.danger,
                fontSize: 13, fontWeight: 500,
              }}>{error}</div>
            )}

            <button onClick={submit} disabled={submitting || loadingMeta} style={{
              marginTop: 22, width: '100%', padding: '14px 0', borderRadius: 10, border: 'none',
              background: submitting ? P.primaryDark : `linear-gradient(135deg, ${P.primary}, ${P.primaryDark})`,
              color: P.white, fontSize: 15, fontWeight: 700, fontFamily: font,
              cursor: submitting || loadingMeta ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 16px rgba(0,114,188,0.25)',
            }}>
              {submitting && <Spinner size={20} color={P.white} />}
              {submitting ? 'Đang gửi...' : 'Xác nhận đặt khám'}
            </button>
          </Card>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: P.textMuted, lineHeight: 1.6 }}>
          Ngã 3 Thái Bảo, Thái Bảo, Nam Sơn, Bắc Ninh — Hotline: 0222 3634 699 — Email: pkkcnquevo@gmail.com<br />
          Copyright © 2026 Phòng Khám Đa Khoa Khu Công Nghiệp Quế Võ
        </div>
      </div>
    </div>
  );
}
